import { BookingFlow } from '@/components/booking/BookingFlow';
import { CompanyInactiveState } from '@/components/company/CompanyInactiveState';
import { CompanyInfoContent, CompanyInfoPanel } from '@/components/company/CompanyInfoPanel';
import { CompanyPageErrorState } from '@/components/company/CompanyPageErrorState';
import { CompanyPageLoadingState } from '@/components/company/CompanyPageLoadingState';
import { CompanyReviews } from '@/components/company/CompanyReviews';
import Header from '@/components/Header';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { companyShowcaseBySlug } from '@/data/company-showcase';
import { useCompanyBySlug } from '@/hooks/queries/useCompanyBySlug';
import { useCompanyProfessionals } from '@/hooks/queries/useCompanyProfessionals';
import { useCompanyServices } from '@/hooks/queries/useCompanyServices';
import { resolveCompanyAttendanceCapabilities } from '@/lib/company/capabilities';
import { buildCompanyClaimWhatsAppUrl } from '@/lib/company/claim';
import { canCompanyAcceptBookings } from '@/lib/company/status';
import { getCompanyOpenStatus, groupCompanyWorkingHours } from '@/lib/company/working-hours';
import { resolveCompanyBanner } from '@/lib/image-placeholders';
import type { CompanyActiveMode, CompanyTab } from '@/types/company-page';
import { ArrowRight, CalendarCheck, ListChecks, MapPin, Package, Star } from 'lucide-react';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';

const CompanyPage = () => {
  const { slug } = useParams<{ slug: string }>();
  const navigate = useNavigate();
  const { company, isLoading, isError, refetch } = useCompanyBySlug(slug);
  const companyCapabilities = resolveCompanyAttendanceCapabilities(company);
  const [currentDateTime, setCurrentDateTime] = useState(() => new Date());
  const [activeSelection, setActiveSelection] = useState<{
    companyId?: string;
    mode?: CompanyActiveMode;
    tab?: CompanyTab;
  }>({});
  const bookingSectionRef = useRef<HTMLElement | null>(null);

  useEffect(() => {
    const intervalId = window.setInterval(() => {
      setCurrentDateTime(new Date());
    }, 60000);

    return () => {
      window.clearInterval(intervalId);
    };
  }, []);

  const fullAddress = useMemo(() => {
    const addressParts = [company?.street, company?.number, company?.neighborhood].filter(Boolean);
    const cityAndState = [company?.city?.name, company?.city?.state?.stateCode].filter(Boolean).join(' - ');

    if (cityAndState) {
      addressParts.push(cityAndState);
    }

    if (company?.postalCode) {
      addressParts.push(`CEP ${company.postalCode}`);
    }

    return addressParts.join(', ');
  }, [company]);

  const companyWorkingHours = useMemo(() => groupCompanyWorkingHours(company?.workingHours), [company?.workingHours]);

  const companyOpenStatus = useMemo(
    () => getCompanyOpenStatus(companyWorkingHours, currentDateTime),
    [companyWorkingHours, currentDateTime],
  );
  const companyClaimWhatsAppUrl = useMemo(
    () => buildCompanyClaimWhatsAppUrl(company?.phoneNumber, company?.name),
    [company?.name, company?.phoneNumber],
  );
  const shouldShowScheduleAction = companyCapabilities.allowScheduledBookings;
  const shouldShowWalkInAction = companyCapabilities.allowWalkInQueue;
  const shouldShowChoiceHeading = shouldShowScheduleAction && shouldShowWalkInAction;
  const defaultActiveMode: CompanyActiveMode = shouldShowScheduleAction
    ? shouldShowWalkInAction
      ? 'choice'
      : 'schedule'
    : shouldShowWalkInAction
      ? 'walk-in'
      : 'choice';
  const defaultActiveTab: CompanyTab = shouldShowScheduleAction ? 'services' : 'reviews';
  const activeMode =
    activeSelection.companyId === company?.id && activeSelection.mode ? activeSelection.mode : defaultActiveMode;
  const activeTab =
    activeSelection.companyId === company?.id && activeSelection.tab ? activeSelection.tab : defaultActiveTab;
  const isWalkInActive = shouldShowWalkInAction && activeMode === 'walk-in';
  const scheduleCompanyId =
    company && canCompanyAcceptBookings(company) && shouldShowScheduleAction ? company.id : undefined;
  const { services } = useCompanyServices(scheduleCompanyId);
  const { professionals } = useCompanyProfessionals(scheduleCompanyId);
  const showcaseData = company ? companyShowcaseBySlug[company.slug] : undefined;

  const handleSchedule = () => {
    setActiveSelection({
      companyId: company?.id,
      mode: 'schedule',
      tab: 'services',
    });
    window.requestAnimationFrame(() => {
      bookingSectionRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    });
  };

  const handleTabChange = (nextTab: string) => {
    const companyTab = nextTab as CompanyTab;

    setActiveSelection((currentSelection) => ({
      companyId: company?.id,
      mode: currentSelection.companyId === company?.id ? currentSelection.mode : undefined,
      tab: companyTab,
    }));
  };

  const handleWalkIn = () => {
    if (!company) {
      return;
    }

    navigate(`/${company.slug}/walk-in`);
  };

  if (isLoading) {
    return <CompanyPageLoadingState />;
  }

  if (isError || !company) {
    return <CompanyPageErrorState onRetry={() => void refetch()} onGoBack={() => navigate(-1)} />;
  }

  if (!canCompanyAcceptBookings(company)) {
    return (
      <CompanyInactiveState
        key={company.id}
        company={company}
        fullAddress={fullAddress}
        whatsappUrl={companyClaimWhatsAppUrl}
      />
    );
  }

  return (
    <div className="bg-background min-h-screen pb-10">
      <Header showBack />

      <div className="relative h-56 overflow-hidden md:h-72 lg:h-80">
        <img src={resolveCompanyBanner(company.coverPhoto)} alt={company.name} className="h-full w-full object-cover" />
        <div className="from-foreground/70 via-foreground/20 absolute inset-0 bg-gradient-to-t to-transparent" />
        <div className="absolute right-0 bottom-0 left-0 p-4 md:p-6">
          <div className="container">
            <h1 className="text-background text-2xl font-bold md:text-3xl">{company.name}</h1>
            <div className="mt-3 flex flex-wrap gap-2.5">
              <div className="bg-background/15 inline-flex items-center gap-1 rounded-full px-3 py-1.5 backdrop-blur-sm">
                <Star className="fill-warning text-warning h-4 w-4" />
                <span className="text-background text-sm font-semibold">{company.rating}</span>
                <span className="text-background/70 text-xs">({company.ratingCount} avaliações)</span>
              </div>
              <div className="bg-background/15 inline-flex items-center gap-1 rounded-full px-3 py-1.5 backdrop-blur-sm">
                <MapPin className="text-background/70 h-3.5 w-3.5" />
                <span className="text-background/80 text-sm">{company.neighborhood}</span>
              </div>
              <span
                className={
                  companyOpenStatus.isOpen
                    ? 'bg-success text-success-foreground inline-flex items-center rounded-full px-3 py-1.5 text-sm font-medium'
                    : 'bg-muted text-muted-foreground inline-flex items-center rounded-full px-3 py-1.5 text-sm font-medium'
                }
              >
                {companyOpenStatus.label}
              </span>
            </div>
          </div>
        </div>
      </div>

      <div className="container mt-6">
        <div className="lg:grid lg:grid-cols-[minmax(0,2fr)_minmax(380px,1fr)] lg:gap-8 xl:grid-cols-[minmax(0,2fr)_minmax(420px,1fr)]">
          <main className="space-y-8">
            {shouldShowChoiceHeading && (
              <section className="space-y-4">
                <div>
                  <h2 className="text-foreground text-xl font-bold">Como quer continuar?</h2>
                  <p className="text-muted-foreground mt-1 text-sm">Escolha como quer ser atendido.</p>
                </div>

                <div className="grid gap-3 sm:grid-cols-2">
                  <button
                    type="button"
                    onClick={handleSchedule}
                    className="border-border bg-card hover:border-primary/40 rounded-xl border p-5 text-left transition-all hover:-translate-y-0.5 hover:shadow-md"
                  >
                    <span className="bg-primary/10 text-primary flex h-11 w-11 items-center justify-center rounded-xl">
                      <CalendarCheck className="h-5 w-5" />
                    </span>
                    <h3 className="text-card-foreground mt-4 text-lg font-semibold">Agendar horário</h3>
                    <p className="text-muted-foreground mt-2 text-sm">Escolha serviço, profissional e horário.</p>
                    <span className="text-card-foreground mt-5 inline-flex items-center gap-2 text-sm font-medium">
                      Ver horários
                      <ArrowRight className="h-4 w-4" />
                    </span>
                  </button>

                  <button
                    type="button"
                    onClick={handleWalkIn}
                    className="border-border bg-card hover:border-primary/40 rounded-xl border p-5 text-left transition-all hover:-translate-y-0.5 hover:shadow-md"
                  >
                    <span className="bg-primary/10 text-primary flex h-11 w-11 items-center justify-center rounded-xl">
                      <ListChecks className="h-5 w-5" />
                    </span>
                    <h3 className="text-card-foreground mt-4 text-lg font-semibold">Fila presencial</h3>
                    <p className="text-muted-foreground mt-2 text-sm">Entre na fila para atendimento no local.</p>
                    <span className="text-card-foreground mt-5 inline-flex items-center gap-2 text-sm font-medium">
                      Entrar na fila
                      <ArrowRight className="h-4 w-4" />
                    </span>
                  </button>
                </div>
              </section>
            )}

            <Tabs value={activeTab} onValueChange={handleTabChange} className="space-y-5">
              <TabsList className="border-border bg-background h-auto w-full justify-start gap-5 overflow-x-auto rounded-none border-b p-0">
                {shouldShowScheduleAction && (
                  <TabsTrigger
                    value="services"
                    className="data-[state=active]:border-primary data-[state=active]:text-primary rounded-none border-b-2 border-transparent px-0 pb-3 data-[state=active]:bg-transparent data-[state=active]:shadow-none"
                  >
                    Serviços
                  </TabsTrigger>
                )}

                {professionals.length > 0 && (
                  <TabsTrigger
                    value="professionals"
                    className="data-[state=active]:border-primary data-[state=active]:text-primary rounded-none border-b-2 border-transparent px-0 pb-3 data-[state=active]:bg-transparent data-[state=active]:shadow-none"
                  >
                    Profissionais
                  </TabsTrigger>
                )}

                <TabsTrigger
                  value="reviews"
                  className="data-[state=active]:border-primary data-[state=active]:text-primary rounded-none border-b-2 border-transparent px-0 pb-3 data-[state=active]:bg-transparent data-[state=active]:shadow-none"
                >
                  Avaliações
                </TabsTrigger>

                {showcaseData?.products.length ? (
                  <TabsTrigger
                    value="products"
                    className="data-[state=active]:border-primary data-[state=active]:text-primary rounded-none border-b-2 border-transparent px-0 pb-3 data-[state=active]:bg-transparent data-[state=active]:shadow-none"
                  >
                    Produtos
                  </TabsTrigger>
                ) : null}

                <TabsTrigger
                  value="info"
                  className="data-[state=active]:border-primary data-[state=active]:text-primary rounded-none border-b-2 border-transparent px-0 pb-3 data-[state=active]:bg-transparent data-[state=active]:shadow-none"
                >
                  Informações
                </TabsTrigger>
              </TabsList>

              {shouldShowScheduleAction && (
                <TabsContent value="services" className="mt-0">
                  <section ref={bookingSectionRef} className="border-border bg-card rounded-xl border p-5">
                    <div className="mb-5">
                      <h2 className="text-card-foreground text-xl font-bold">Serviços</h2>
                      <p className="text-muted-foreground mt-1 text-sm">Escolha o que quer fazer.</p>
                    </div>
                    <BookingFlow
                      company={company}
                      enabled={activeTab === 'services'}
                      onWalkInSelected={handleWalkIn}
                      professionals={professionals}
                      requireEntryModeSelection={shouldShowChoiceHeading}
                      services={services}
                      variant="embedded"
                    />
                  </section>
                </TabsContent>
              )}

              {isWalkInActive && (
                <section className="border-border bg-card rounded-xl border p-5">
                  <span className="bg-primary/10 text-primary flex h-11 w-11 items-center justify-center rounded-xl">
                    <ListChecks className="h-5 w-5" />
                  </span>
                  <h2 className="text-card-foreground mt-4 text-xl font-bold">Fila presencial</h2>
                  <p className="text-muted-foreground mt-2 text-sm">Entre na fila para atendimento no local.</p>
                  <button
                    type="button"
                    onClick={handleWalkIn}
                    className="bg-primary text-primary-foreground mt-5 inline-flex h-12 items-center justify-center gap-2 rounded-xl px-5 text-sm font-semibold transition-all hover:opacity-90"
                  >
                    Fila presencial
                    <ArrowRight className="h-4 w-4" />
                  </button>
                </section>
              )}

              {professionals.length > 0 && (
                <TabsContent value="professionals" className="mt-0">
                  <section className="space-y-3">
                    <h2 className="text-foreground text-xl font-bold">Profissionais</h2>
                    <div className="grid gap-3 sm:grid-cols-2">
                      {professionals.map((professional) => (
                        <div
                          key={professional.id}
                          className="border-border bg-card flex items-center gap-4 rounded-xl border p-4"
                        >
                          <img
                            src={professional.photo ?? '/person-placeholder.jpg'}
                            alt={professional.name}
                            className="h-14 w-14 shrink-0 rounded-full object-cover"
                          />
                          <div className="min-w-0">
                            <h3 className="text-card-foreground text-[15px] font-semibold">{professional.name}</h3>
                            <div className="mt-1 flex items-center gap-1">
                              <Star className="fill-warning text-warning h-3.5 w-3.5" />
                              <span className="text-card-foreground text-sm font-medium">{professional.rating}</span>
                              <span className="text-muted-foreground text-xs">({professional.feedbackCount})</span>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </section>
                </TabsContent>
              )}

              <TabsContent value="reviews" className="mt-0">
                <CompanyReviews
                  companyId={company.id}
                  enabled={activeTab === 'reviews'}
                  fallbackRating={company.rating}
                  fallbackTotal={company.ratingCount}
                />
              </TabsContent>

              {showcaseData?.products.length ? (
                <TabsContent value="products" className="mt-0">
                  <section className="space-y-3">
                    <h2 className="text-foreground text-xl font-bold">Produtos</h2>
                    <div className="grid gap-3 sm:grid-cols-2">
                      {showcaseData.products.map((product) => (
                        <div key={product.id} className="border-border bg-card rounded-xl border p-4">
                          <Package className="text-primary h-5 w-5" />
                          <p className="text-card-foreground mt-3 font-semibold">{product.name}</p>
                          <p className="text-muted-foreground mt-1 text-sm">
                            R$ {product.price.toFixed(2).replace('.', ',')}
                          </p>
                        </div>
                      ))}
                    </div>
                  </section>
                </TabsContent>
              ) : null}

              <TabsContent value="info" className="mt-0">
                <section className="border-border bg-card space-y-4 rounded-xl border p-5 lg:hidden">
                  <CompanyInfoContent
                    currentDateTime={currentDateTime}
                    fullAddress={fullAddress}
                    latitude={company.latitude}
                    longitude={company.longitude}
                    phoneNumber={company.phoneNumber}
                    showcaseData={showcaseData}
                    workingHours={companyWorkingHours}
                  />
                </section>
                <section className="border-border bg-card hidden rounded-xl border p-5 lg:block">
                  <h2 className="text-card-foreground text-xl font-bold">Informações</h2>
                  <p className="text-muted-foreground mt-2 text-sm">
                    Localização, contato e detalhes para se preparar antes do atendimento.
                  </p>
                </section>
              </TabsContent>
            </Tabs>
          </main>

          <aside className="hidden lg:block">
            <div className="sticky top-20">
              <CompanyInfoPanel
                currentDateTime={currentDateTime}
                fullAddress={fullAddress}
                latitude={company.latitude}
                longitude={company.longitude}
                phoneNumber={company.phoneNumber}
                showcaseData={showcaseData}
                workingHours={companyWorkingHours}
              />
            </div>
          </aside>
        </div>
      </div>
    </div>
  );
};

export default CompanyPage;
